import { useState, useEffect } from "react";
import { Button, Form, Input, Modal, Select } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DatePicker, useJalaliLocaleListener } from "antd-jalali";
import dayjs from "dayjs";
import Moment from "moment-jalaali";

import { Modals } from "scripts/settings";
import Resources from "scripts/resources";
import Rules from "scripts/rules";
import API from "scripts/api";

import InputCurrency from "components/input-currency";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate, options: { jobs } }) => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { submitting, visible } = state;
	const { id, title } = data;
	const { hash } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values.paymentDate = Moment(values.paymentDate.$d).format(
					process.env.REACT_APP_DATE_FORMAT
				);

				values = ObjectNumbersToEnglish(values);

				const action = id
					? API.payment.update(id, values)
					: API.payment.create(values);

				action
					.then(() => {
						onUpdate(null, true);
						navigate(-1);
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		if (hash === `#${Modals.PAYMENT}_${Modals.EDIT}`) {
			form.setFieldsValue({
				...data,
				jobId: data?.job.id,
				paymentDate: dayjs(data.paymentDate),
			});
			setState((prevState) => ({
				...prevState,
				visible: true,
			}));
		} else if (hash === `#${Modals.PAYMENT}`) {
			form.resetFields();
			setState((prevState) => ({
				...prevState,
				visible: true,
			}));
		} else {
			setState(initialState);
		}
	};

	useEffect(componentDidUpdate, [hash]);

	useJalaliLocaleListener();

	return (
		<Modal
			className="bk-modal-payment"
			footer={
				<>
					<Button type="primary" onClick={handleSubmit} loading={submitting}>
						{Resources.confirm}
					</Button>
					<Button onClick={() => navigate(-1)}>{Resources.cancel}</Button>
				</>
			}
			maskClosable={false}
			onCancel={() => navigate(-1)}
			open={visible}
			width={800}
			destroyOnClose
			title={
				id
					? `${Resources.edit} ${Resources.pay}${Resources.y}`
					: `${Resources.add} ${Resources.pay}${Resources.y}`
			}
		>
			<Form layout="vertical" form={form}>
				<div className="row">
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="subject"
							label={Resources.subject}
							rules={Rules.required}
						>
							<Select
								options={[
									{ value: "ladder", label: Resources.manageModal.ladderAd },
									{
										value: "special_jobs",
										label: `${Resources.jobs} ${Resources.special}`,
									},
									{
										value: "subscription",
										label: `${Resources.buy} ${Resources.subscription}`,
									},
								]}
							/>
						</Form.Item>
					</div>
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="jobId"
							label={Resources.job}
							rules={Rules.required}
						>
							<Select options={jobs} optionFilterProp="label" showSearch />
						</Form.Item>
					</div>
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="depositedTo"
							label={`${Resources.deposit} ${Resources.toBe} ${Resources.bankCard}`}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
					</div>
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="amount"
							label={Resources.amount}
							rules={Rules.required}
						>
							<InputCurrency />
						</Form.Item>
					</div>
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="traceNumber"
							label={`${Resources.code} ${Resources.tracking}`}
							rules={Rules.required}
						>
							<Input />
						</Form.Item>
					</div>
					<div className="col-sm-12 col-md-6">
						<Form.Item
							name="paymentDate"
							label={`${Resources.date} ${Resources.pay}`}
							rules={Rules.required}
						>
							<DatePicker />
						</Form.Item>
					</div>
				</div>
			</Form>
		</Modal>
	);
};

export default Component;
